import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { documentApi, workspaceApi } from '../api/apiClient';
import { CloseIcon, DownloadIcon, FileIcon, OpenExternalIcon, PaperclipIcon, TrashIcon } from '../components/AppIcons';
import './DocumentLibrary.css';

export default function DocumentLibrary() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const versionFileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [versions, setVersions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [extractedText, setExtractedText] = useState(null);
  const [extractedLoading, setExtractedLoading] = useState(false);
  const [extractedOpen, setExtractedOpen] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [metaText, setMetaText] = useState('{}');
  const [metaSaving, setMetaSaving] = useState(false);
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [textUploading, setTextUploading] = useState(false);
  const [focusChunkId, setFocusChunkId] = useState(null);
  const [imagePreviewUrls, setImagePreviewUrls] = useState({});
  const [imagePreviewFailedIds, setImagePreviewFailedIds] = useState({});
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [versionBaseDoc, setVersionBaseDoc] = useState(null);
  const previewUrlsRef = useRef({});

  const canEditDocuments = workspace && ['OWNER', 'EDITOR', 'ADMIN'].includes(workspace.userRole);

  useEffect(() => {
    loadDocuments();
  }, [workspaceId]);

  useEffect(() => {
    const focusDocumentId = Number(searchParams.get('documentId'));
    const focusChunk = Number(searchParams.get('chunkId'));
    if (!Number.isFinite(focusDocumentId)) return;
    if (!documents.length) return;
    const doc = documents.find((d) => d.id === focusDocumentId);
    if (!doc) return;
    setFocusChunkId(Number.isFinite(focusChunk) ? focusChunk : null);
    setSelectedDoc(doc);
    (async () => {
      try {
        const res = await documentApi.getChunks(workspaceId, doc.id);
        setChunks(res.data);
      } catch (err) {
        console.error('Failed to load chunks', err);
      }
    })();
  }, [documents, searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const res = await workspaceApi.get(workspaceId);
        setWorkspace(res.data);
      } catch {
        setWorkspace(null);
      }
    })();
  }, [workspaceId]);

  useEffect(() => {
    setExtractedText(null);
    setExtractedOpen(false);
  }, [selectedDoc?.id]);

  useEffect(() => {
    if (!selectedDoc?.id) {
      setVersions([]);
      return;
    }
    (async () => {
      try {
        const res = await documentApi.versions(workspaceId, selectedDoc.id);
        setVersions(Array.isArray(res.data) ? res.data : []);
      } catch {
        setVersions([]);
      }
    })();
  }, [workspaceId, selectedDoc?.id]);

  useEffect(() => {
    if (!selectedDoc) return;
    if (selectedDoc.metadata) {
      try {
        const j = typeof selectedDoc.metadata === 'string' ? JSON.parse(selectedDoc.metadata) : selectedDoc.metadata;
        setMetaText(JSON.stringify(j, null, 2));
      } catch {
        setMetaText(String(selectedDoc.metadata));
      }
    } else {
      setMetaText('{\n  "tags": [],\n  "department": "",\n  "validUntil": ""\n}');
    }
  }, [selectedDoc?.id, selectedDoc?.metadata]);

  useEffect(() => {
    const imageDocs = documents.filter((doc) => isImageDoc(doc));
    const missing = imageDocs.filter((doc) => !imagePreviewUrls[doc.id] && !imagePreviewFailedIds[doc.id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const next = {};
      const failed = {};
      for (const doc of missing) {
        try {
          const res = await documentApi.getFileBlob(workspaceId, doc.id, true);
          const contentType = res.headers?.['content-type'] || doc.mimeType || 'image/*';
          const blob = new Blob([res.data], { type: contentType });
          next[doc.id] = URL.createObjectURL(blob);
        } catch (err) {
          failed[doc.id] = true;
          if (err?.response?.status !== 404) {
            console.error('Preview load failed', err);
          }
        }
      }
      if (!cancelled && Object.keys(next).length > 0) {
        setImagePreviewUrls((prev) => {
          const merged = { ...prev, ...next };
          previewUrlsRef.current = merged;
          return merged;
        });
      } else if (cancelled) {
        Object.values(next).forEach((url) => URL.revokeObjectURL(url));
      }
      if (!cancelled && Object.keys(failed).length > 0) {
        setImagePreviewFailedIds((prev) => ({ ...prev, ...failed }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documents, imagePreviewUrls, imagePreviewFailedIds, workspaceId]);

  useEffect(() => () => {
    Object.values(previewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const loadDocuments = async () => {
    try {
      setErrorMessage('');
      const res = await documentApi.list(workspaceId);
      setDocuments(res.data);
    } catch (err) {
      console.error('Failed to load documents', err);
      if (err?.response?.status === 403) {
        setErrorMessage(err?.response?.data?.message || 'You do not have access to this workspace.');
      } else {
        setErrorMessage('Failed to load documents.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      await documentApi.uploadBatch(workspaceId, files, (progressEvent) => {
        if (!progressEvent?.total) return;
        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(pct);
      });
      await loadDocuments();
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUploadVersion = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !versionBaseDoc?.id) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      await documentApi.upload(workspaceId, file, (progressEvent) => {
        if (!progressEvent?.total) return;
        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(pct);
      }, versionBaseDoc.id);
      await loadDocuments();
      const refreshed = await documentApi.get(workspaceId, versionBaseDoc.id);
      setSelectedDoc(refreshed.data);
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Failed to upload new version.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setVersionBaseDoc(null);
      if (versionFileInputRef.current) versionFileInputRef.current.value = '';
    }
  };

  const handleUploadText = async () => {
    const body = textContent.trim();
    if (!body) {
      window.alert('Paste some text first.');
      return;
    }
    setTextUploading(true);
    try {
      await documentApi.uploadText(workspaceId, textTitle.trim() || 'Pasted note', body);
      setTextTitle('');
      setTextContent('');
      await loadDocuments();
    } catch (err) {
      console.error('Text upload failed', err);
      window.alert(err?.response?.data?.message || 'Failed to save pasted text.');
    } finally {
      setTextUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document and all its chunks?')) return;
    try {
      await documentApi.delete(workspaceId, docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      if (selectedDoc?.id === docId) setSelectedDoc(null);
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const viewChunks = async (doc) => {
    setSelectedDoc(doc);
    try {
      const res = await documentApi.getChunks(workspaceId, doc.id);
      setChunks(res.data);
    } catch (err) {
      console.error('Failed to load chunks', err);
    }
  };

  const downloadOriginal = async (doc) => {
    try {
      const res = await documentApi.getFileBlob(workspaceId, doc.id, false);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalName || 'document';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  const openOriginal = async (doc) => {
    try {
      const res = await documentApi.getFileBlob(workspaceId, doc.id, true);
      const ct = res.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type: ct });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (err) {
      console.error('Open file failed', err);
    }
  };

  const handleReprocess = async () => {
    if (!selectedDoc || !canEditDocuments) return;
    if (!window.confirm('Re-run extraction, chunking, and embeddings for this file?')) return;
    try {
      await documentApi.reprocess(workspaceId, selectedDoc.id);
      window.alert('Reprocessing started. Wait a few seconds and refresh the list.');
    } catch (err) {
      console.error(err);
      window.alert(err?.response?.data?.message || 'Could not start reprocessing.');
    }
  };

  const handleExcludedFromSearchChange = async (checked) => {
    if (!selectedDoc || !canEditDocuments) return;
    try {
      await documentApi.patch(workspaceId, selectedDoc.id, { excludedFromSearch: checked });
      setSelectedDoc((prev) => (prev ? { ...prev, excludedFromSearch: checked } : prev));
      setDocuments((prev) =>
        prev.map((d) => (d.id === selectedDoc.id ? { ...d, excludedFromSearch: checked } : d))
      );
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Could not update search visibility.');
    }
  };

  const handleSaveMetadata = async () => {
    if (!selectedDoc || !canEditDocuments) return;
    let parsed;
    try {
      parsed = JSON.parse(metaText);
    } catch {
      window.alert('Metadata must be valid JSON.');
      return;
    }
    setMetaSaving(true);
    try {
      await documentApi.patch(workspaceId, selectedDoc.id, { metadata: parsed });
      await loadDocuments();
      const res = await documentApi.get(workspaceId, selectedDoc.id);
      setSelectedDoc(res.data);
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Save failed.');
    } finally {
      setMetaSaving(false);
    }
  };

  const loadExtractedText = async () => {
    if (!selectedDoc) return;
    setExtractedLoading(true);
    try {
      const res = await documentApi.getExtractedText(workspaceId, selectedDoc.id);
      setExtractedText(typeof res.data === 'string' ? res.data : '');
      setExtractedOpen(true);
    } catch (err) {
      console.error('Failed to load extracted text', err);
    } finally {
      setExtractedLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      UPLOADED: 'v-badge--neutral',
      PROCESSING: 'v-badge--warning',
      CHUNKED: 'v-badge--success',
      INDEXED: 'v-badge--success',
      FAILED: 'v-badge--error',
    };
    return `v-badge ${map[status] || 'v-badge--neutral'}`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const isImageDoc = (doc) => (doc?.mimeType || '').toLowerCase().startsWith('image/');

  if (loading) return <div className="v-loading">Loading documents...</div>;
  if (errorMessage) {
    return (
      <div className="v-page">
        <div className="v-empty">
          <h3>Documents unavailable</h3>
          <p>{errorMessage}</p>
          <button className="v-btn v-btn--primary" onClick={() => navigate('/workspaces')}>
            Back to workspaces
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="v-page">
      {/* Breadcrumb */}
      <div className="v-breadcrumb">
        <button onClick={() => navigate('/workspaces')}>Workspaces</button>
        <span>/</span>
        <button type="button" onClick={() => navigate(`/workspaces/${workspaceId}`)}>Dashboard</button>
        <span>/</span>
        <button type="button" onClick={() => navigate(`/workspaces/${workspaceId}/search`)}>Search</button>
        <span>/</span>
        <span>Documents</span>
      </div>

      <div className="v-page__header">
        <div>
          <h1 className="v-page__title">Knowledge Base Uploads</h1>
          <p className="v-page__subtitle">{documents.length} source document{documents.length !== 1 ? 's' : ''} available for chat and search</p>
        </div>
        <div className="v-page__actions">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md,.csv,.xlsx,.jpg,.jpeg,.png"
            multiple
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
          <input
            ref={versionFileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md,.csv,.xlsx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleUploadVersion}
          />
          <button
            className="v-btn v-btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || textUploading}
          >
            <span className="v-btn__icon"><PaperclipIcon /></span>
            {uploading ? `Uploading ${uploadProgress}%` : 'Upload document(s)'}
          </button>
        </div>
      </div>

      {canEditDocuments && (
        <section className="v-text-upload">
          <div className="v-text-upload__row">
            <input
              type="text"
              className="v-text-upload__title"
              value={textTitle}
              onChange={(e) => setTextTitle(e.target.value)}
              placeholder="Optional title (e.g. Call notes - ACME)"
            />
            <button
              type="button"
              className="v-btn v-btn--secondary"
              onClick={handleUploadText}
              disabled={textUploading || uploading || !textContent.trim()}
            >
              {textUploading ? 'Saving text…' : 'Save pasted text as document'}
            </button>
          </div>
          <textarea
            className="v-text-upload__textarea"
            rows={5}
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            placeholder="Paste plain text here. It will be stored as a text document, chunked, and available for Q&A."
          />
        </section>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="v-upload-progress">
          <div className="v-upload-progress__bar" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}

      {/* Document list */}
      {documents.length === 0 ? (
        <div className="v-empty">
          <div className="v-empty__icon"><FileIcon /></div>
          <h3>No documents yet</h3>
          <p>Upload your first document to start the chunking pipeline.</p>
          {canEditDocuments && (
            <button className="v-btn v-btn--primary" onClick={() => fileInputRef.current?.click()}>
              Upload document
            </button>
          )}
        </div>
      ) : (
        <div className="v-doc-layout">
          <div className="v-doc-list">
            <table className="v-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Chunks</th>
                  <th>Status</th>
                  <th>Uploaded</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    className={selectedDoc?.id === doc.id ? 'v-table__row--selected' : ''}
                    onClick={() => viewChunks(doc)}
                  >
                    <td className="v-table__name">
                      <span className="v-table__file-icon"><FileIcon /></span>
                      {isImageDoc(doc) && imagePreviewUrls[doc.id] && (
                        <img
                          src={imagePreviewUrls[doc.id]}
                          alt={doc.originalName || 'Image thumbnail'}
                          className="v-doc-thumb"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLightboxUrl(imagePreviewUrls[doc.id]);
                          }}
                        />
                      )}
                      {doc.originalName}
                      {doc.excludedFromSearch && (
                        <span className="v-badge v-badge--neutral v-doc-excluded-badge" title="Excluded from search and Q&A">
                          excluded
                        </span>
                      )}
                    </td>
                    <td>{formatSize(doc.fileSize)}</td>
                    <td>{doc.chunkCount}</td>
                    <td>
                      <span className={statusBadge(doc.status)}>{doc.status}</span>
                      <div className="v-table__status-message" title={doc.latestVersion ? 'Current version' : 'Older version'}>
                        v{doc.versionNumber || 1}{doc.latestVersion ? ' (latest)' : ' (archived)'}
                      </div>
                      {doc.statusMessage && (
                        <div className="v-table__status-message" title={doc.statusMessage}>
                          {doc.statusMessage}
                        </div>
                      )}
                    </td>
                    <td>{new Date(doc.createdAt).toLocaleDateString()}</td>
                    <td>
                      {canEditDocuments && (
                        <button
                          className="v-btn v-btn--ghost v-btn--sm"
                          onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                          title="Delete document"
                        >
                          <span className="v-btn__icon"><TrashIcon /></span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chunk viewer */}
          {selectedDoc && (
            <div className="v-chunk-panel">
              <div className="v-chunk-panel__header">
                <h3>{selectedDoc.originalName}</h3>
                <button className="v-btn v-btn--ghost v-btn--sm" onClick={() => setSelectedDoc(null)} title="Close panel">
                  <span className="v-btn__icon"><CloseIcon /></span>
                </button>
              </div>
              <p className="v-chunk-panel__info">
                {chunks.length} chunk{chunks.length !== 1 ? 's' : ''} • {formatSize(selectedDoc.fileSize)}
              </p>
              {selectedDoc.statusMessage && (
                <p className="v-chunk-panel__info" style={{ color: '#b91c1c' }}>
                  {selectedDoc.statusMessage}
                </p>
              )}
              <div className="v-chunk-panel__actions">
                <button
                  type="button"
                  className="v-btn v-btn--ghost v-btn--sm"
                  onClick={() => openOriginal(selectedDoc)}
                  title="Open original file in a new tab"
                >
                  <span className="v-btn__icon"><OpenExternalIcon /></span>
                  Open original
                </button>
                <button
                  type="button"
                  className="v-btn v-btn--ghost v-btn--sm"
                  onClick={() => downloadOriginal(selectedDoc)}
                  title="Download the original file"
                >
                  <span className="v-btn__icon"><DownloadIcon /></span>
                  Download
                </button>
                {canEditDocuments && (
                  <button type="button" className="v-btn v-btn--ghost v-btn--sm" onClick={handleReprocess}>
                    Re-process
                  </button>
                )}
                {canEditDocuments && (
                  <button
                    type="button"
                    className="v-btn v-btn--ghost v-btn--sm"
                    onClick={() => {
                      setVersionBaseDoc(selectedDoc);
                      versionFileInputRef.current?.click();
                    }}
                    title="Upload a new version of this document"
                  >
                    Upload new version
                  </button>
                )}
              </div>
              {versions.length > 0 && (
                <div className="v-doc-meta-block">
                  <label className="v-doc-meta-block__label">Version history</label>
                  <div className="v-chunk-list">
                    {versions.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        className="v-btn v-btn--ghost v-btn--sm"
                        onClick={() => viewChunks(v)}
                        style={{ justifyContent: 'space-between' }}
                      >
                        <span>v{v.versionNumber || 1} {v.latestVersion ? '(latest)' : ''}</span>
                        <span>{new Date(v.createdAt).toLocaleDateString()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {isImageDoc(selectedDoc) && imagePreviewUrls[selectedDoc.id] && (
                <div className="v-image-preview">
                  <img
                    src={imagePreviewUrls[selectedDoc.id]}
                    alt={selectedDoc.originalName || 'Image preview'}
                    className="v-image-preview__thumb"
                    onClick={() => setLightboxUrl(imagePreviewUrls[selectedDoc.id])}
                  />
                  <p className="v-image-preview__hint">Click image to enlarge</p>
                </div>
              )}
              {canEditDocuments && (
                <label className="v-doc-exclude-row">
                  <input
                    type="checkbox"
                    checked={!!selectedDoc.excludedFromSearch}
                    onChange={(e) => handleExcludedFromSearchChange(e.target.checked)}
                  />
                  <span>Exclude from search &amp; Q&amp;A (keep file in library)</span>
                </label>
              )}
              {canEditDocuments && (
                <div className="v-doc-meta-block">
                  <label className="v-doc-meta-block__label" htmlFor="doc-meta-json">Metadata (JSON: tags, department, validUntil)</label>
                  <textarea
                    id="doc-meta-json"
                    className="v-doc-meta-block__textarea"
                    value={metaText}
                    onChange={(e) => setMetaText(e.target.value)}
                    rows={5}
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="v-btn v-btn--primary v-btn--sm"
                    onClick={handleSaveMetadata}
                    disabled={metaSaving}
                  >
                    {metaSaving ? 'Saving…' : 'Save metadata'}
                  </button>
                </div>
              )}
              <div className="v-extracted-block">
                <button
                  type="button"
                  className="v-btn v-btn--ghost v-btn--sm v-extracted-block__toggle"
                  onClick={() => {
                    if (extractedOpen && extractedText !== null) {
                      setExtractedOpen(false);
                    } else if (extractedText !== null) {
                      setExtractedOpen(true);
                    } else {
                      loadExtractedText();
                    }
                  }}
                  disabled={extractedLoading}
                >
                  {extractedLoading ? 'Loading text…' : extractedOpen ? 'Hide full extracted text' : 'Show full extracted text'}
                </button>
                {extractedOpen && extractedText !== null && (
                  <pre className="v-extracted-block__pre">{extractedText || '— No extracted text yet (processing may still be running). —'}</pre>
                )}
              </div>
              <div className="v-chunk-list">
                {chunks.map((chunk) => (
                  <div
                    key={chunk.id}
                    className={`v-chunk-item ${focusChunkId === chunk.id ? 'v-chunk-item--focus' : ''}`}
                  >
                    <div className="v-chunk-item__header">
                      <span className="v-chunk-item__index">Chunk {chunk.chunkIndex + 1}</span>
                      <span className="v-chunk-item__tokens">{chunk.tokenCount} tokens</span>
                    </div>
                    <p className="v-chunk-item__content">{chunk.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {lightboxUrl && (
        <div className="v-img-lightbox" onClick={() => setLightboxUrl(null)} role="presentation">
          <button
            type="button"
            className="v-img-lightbox__close"
            onClick={() => setLightboxUrl(null)}
            title="Close image preview"
          >
            <CloseIcon />
          </button>
          <img src={lightboxUrl} alt="Enlarged preview" className="v-img-lightbox__img" />
        </div>
      )}
    </div>
  );
}
